import { getCookie } from "@workspace/cookie-manager";
import { getPersistedUTMs } from "@/lib/utm-tracker";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

/**
 * Build the checkout redirect URL.
 *
 * Points at the Node checkout endpoint (`{host}/checkout/{slug}`). The host comes
 * from NEXT_PUBLIC_API_WEB_HOST (the Node API host, set per environment via
 * the deploy workflow build-args), falling back to prod Node. Set it to "" to fall
 * back to the Rails route (`{NEXT_PUBLIC_INITO_HOST}/en-us/checkout/{slug}`).
 *
 * Consent (mirrors Rails orders#checkout):
 * - The advertising_cookie_consent cookie is host-scoped and can't reach the
 *   Node checkout subdomain, so we propagate it as `cy-ad` (1/0), which the Node
 *   service maps to `uc_ad` on the Shopify URL.
 * - UTMs are forwarded only when advertising consent is granted; otherwise they
 *   are stripped (Rails only has UTM values when consented).
 *
 * Note: the Node endpoint has no `/en-us` prefix and lives at `/checkout/{slug}`.
 */
const CHECKOUT_API_HOST = (
  process.env.NEXT_PUBLIC_API_WEB_HOST || "https://api-web.inito.com"
).replace(/\/$/, "");

export function buildCheckoutUrl(slug: string, query?: string): string {
  const params = new URLSearchParams(query ?? "");
  const adConsent = getCookie("advertising_cookie_consent");

  // Fill in UTMs from persisted (consent-gated) cookies when the live URL
  // doesn't already carry them.
  for (const [key, value] of Object.entries(getPersistedUTMs())) {
    if (!params.get(key)) params.set(key, value);
  }

  // Forward UTMs only with advertising consent; strip them otherwise.
  if (adConsent !== "allow") {
    for (const key of UTM_KEYS) params.delete(key);
  }

  // Propagate advertising consent cross-subdomain via cy-ad -> uc_ad on Node.
  if (adConsent === "allow") params.set("cy-ad", "1");
  else if (adConsent === "deny") params.set("cy-ad", "0");

  const qs = params.toString();
  const base = CHECKOUT_API_HOST
    ? `${CHECKOUT_API_HOST.replace(/\/$/, "")}/checkout/${slug}`
    : `${process.env.NEXT_PUBLIC_INITO_HOST}/en-us/checkout/${slug}`;

  return qs ? `${base}?${qs}` : base;
}
